import React, { useState } from "react";
import Select, { components } from "react-select";
import { getIn, useFormikContext } from "formik";
import customStyles from "./customStyles";
import { CustomControlIconError } from "./CustomControlIconError";
import { CustomDropdownIndicator } from "./CustomDropdownIndicator";
import { useTranslation } from "react-i18next";
import { defaultTrimmedFilterOption } from "./defaultTrimmedFilterOption";

export interface BaseOption<T = string | number> {
    value: T;
    label: string;
}

export type Option<T = {}> = BaseOption & T;

const InputWithAutoComplete = (props: any) => {
    const autoComplete = props.selectProps.inputAutoComplete ?? "off";
    return <components.Input {...props} autoComplete={autoComplete} />;
};

interface ReactSelectProps {
    name?: string;
    value?: Option | number | null;
    options: Array<any>;
    placeholder: string;
    onChange: (option: Option | null) => void;
    isSearchable: boolean;
    disabled?: boolean;
    key?: string;
    stylesSelect?: any;
    menuPlacement?: string;
    menuPortalTarget?: HTMLElement;
    id?: string;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    formatOptionLabel?: (option: BaseOption, context?: { context: "menu" | "value" }) => React.ReactNode;
    hasError?: boolean;
    customFilterOption?: (option: Option, inputValue: string) => boolean;
    searchPlaceholder?: string;
    floatingLabel?: string;
    isMeMenu?: boolean;
    isTitle?: boolean;
    errorIconRightOffset?: string;
    inputAutoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
    isLoading?: boolean;
    noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode;
}

const ReactSelect: React.FC<ReactSelectProps> = ({
    value,
    options,
    placeholder,
    onChange,
    isSearchable,
    disabled = false,
    key,
    name,
    menuPlacement,
    stylesSelect,
    menuPortalTarget = document.body,
    id,
    onKeyDown,
    formatOptionLabel,
    hasError,
    customFilterOption,
    searchPlaceholder = "Search",
    floatingLabel,
    isMeMenu,
    isTitle,
    errorIconRightOffset = "34px",
    inputAutoComplete = "off",
    isLoading,
    noOptionsMessage,
}) => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");

    // If select used in Formik, then we pass it to 'name' and then we can use the useFormikContext
    let errors: any;
    let touched: any;

    const formikContext = useFormikContext();
    if (name) {
        // `getIn` is used to handle both regular and nested field names
        errors = formikContext?.errors && getIn(formikContext.errors, name);
        touched = formikContext?.touched && getIn(formikContext.touched, name);
    }

    if (hasError) {
        errors = hasError;
        touched = hasError;
    }

    const CustomPlaceholder = (props: any) => {
        const showSearchPlaceholder = isSearchable && isMenuOpen && inputValue === "";
        return (
            <components.Placeholder {...props}>
                {showSearchPlaceholder ? t(searchPlaceholder) : placeholder}
            </components.Placeholder>
        );
    };

    const CustomSingleValue = (props: any) => {
        const showSearchPlaceholder = isSearchable && isMenuOpen && inputValue === "";
        if (showSearchPlaceholder) {
            return (
                <components.SingleValue {...props}>
                    <span className="text-grey">{t(searchPlaceholder)}</span>
                </components.SingleValue>
            );
        }
        return <components.SingleValue {...props} />;
    };

    return (
        <>
            <Select
                id={id}
                key={key}
                inputId={id ?? name}
                name={name}
                value={value}
                options={options}
                placeholder={placeholder}
                onChange={onChange}
                isSearchable={isSearchable}
                inputAutoComplete={inputAutoComplete}
                styles={stylesSelect ? stylesSelect : customStyles(errors, touched, isMeMenu, isTitle)}
                isDisabled={disabled}
                filterOption={customFilterOption ? customFilterOption : defaultTrimmedFilterOption}
                menuPlacement={menuPlacement}
                menuPortalTarget={menuPortalTarget}
                inputValue={inputValue}
                onInputChange={(newValue, { action }) => {
                    if (action !== "menu-close") {
                        setInputValue(newValue);
                    }
                }}
                onMenuOpen={() => setIsMenuOpen(true)}
                onMenuClose={() => {
                    setIsMenuOpen(false);
                    setInputValue("");
                }}
                components={{
                    Control: CustomControlIconError,
                    DropdownIndicator: CustomDropdownIndicator,
                    Placeholder: CustomPlaceholder,
                    SingleValue: CustomSingleValue,
                    Input: InputWithAutoComplete,
                }}
                formatOptionLabel={formatOptionLabel}
                onKeyDown={onKeyDown}
                error={errors && touched}
                floatingLabel={floatingLabel}
                data-error-field={name}
                errorIconRightOffset={errorIconRightOffset}
                isLoading={isLoading}
                {...(noOptionsMessage ? { noOptionsMessage } : {})}
            />
        </>
    );
};

export default ReactSelect;
